import type { AtBatResult, PitchEntry } from '../state/selectors';
import AtBatResultBadge from './AtBatResultBadge';
import PlayerAvatar from './PlayerAvatar';
import type { PlayerSeed } from './PlayerModal';
import { getInitials } from '../utils/initials';
import styles from './PitchLog.module.css';

export interface PitchLogPersonInfo {
  id: number;
  name: string;
  jersey: string | null;
}

interface PitchLogProps {
  pitches: PitchEntry[];
  /** Optional click handler for the at-bat result text. Opens the inning modal. */
  onResultClick?: () => void;
  /** Optional label for the result button (defaults to "Open at-bat detail"). */
  resultClickLabel?: string;
  /** Just-completed at-bat result; rendered as a badge in the header. */
  atBatResult?: AtBatResult | null;
  /** Batter chip (left of the title) — name + jersey + avatar. */
  batter?: PitchLogPersonInfo | null;
  /** Pitcher chip (right of the title) — avatar + initials + jersey. */
  pitcher?: PitchLogPersonInfo | null;
  /** Click handler for batter/pitcher chips — receives the full seed so the
   *  PlayerModal heading paints synchronously from props. */
  onPlayerClick?: (seed: PlayerSeed) => void;
  /** Step back to the previous at-bat. Disabled when undefined. */
  onPrev?: () => void;
  /** Step forward to the next at-bat. Disabled when undefined. */
  onNext?: () => void;
  /** Snap to the latest at-bat. Disabled when undefined (already at latest). */
  onLatest?: () => void;
  /** True if the displayed at-bat is the latest one. Hides "Latest" CTA when true. */
  isLatest?: boolean;
  canPrev?: boolean;
  canNext?: boolean;
}

function rowClassFor(p: PitchEntry): string {
  // Only in-play (the "something happened") pitches stand out. Balls and
  // strikes carry the same neutral edge — less noise, same at-a-glance signal.
  const isInPlay = !p.isStrike && !p.isBall;
  return `${styles.row} ${isInPlay ? styles.rowInPlay : styles.rowNeutral}`;
}

function fmt(value: number | null, unit?: string, digits = 0): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value.toFixed(digits)}${unit ?? ''}`;
}

function fmtSpeed(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value.toFixed(1)} mph`;
}

function BatterChip({
  info,
  onClick,
}: {
  info: PitchLogPersonInfo;
  onClick?: (seed: PlayerSeed) => void;
}): JSX.Element {
  const content = (
    <>
      <PlayerAvatar
        personId={info.id}
        name={info.name}
        size={240}
        displaySize={32}
        className={styles.chipAvatar}
      />
      <span className={styles.chipBody}>
        <span className={styles.chipName}>{info.name}</span>
        {info.jersey && <span className={styles.chipJersey}>#{info.jersey}</span>}
      </span>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={() => onClick({ id: info.id, name: info.name, jersey: info.jersey })}
        className={`${styles.chip} ${styles.chipBatter} ${styles.chipButton}`}
        data-testid="pitch-log-batter-chip"
        aria-label={`Open profile for ${info.name}`}
      >
        {content}
      </button>
    );
  }
  return (
    <span
      className={`${styles.chip} ${styles.chipBatter}`}
      data-testid="pitch-log-batter-chip"
    >
      {content}
    </span>
  );
}

function PitcherChip({
  info,
  onClick,
}: {
  info: PitchLogPersonInfo;
  onClick?: (seed: PlayerSeed) => void;
}): JSX.Element {
  const initials = getInitials(info.name);
  const content = (
    <>
      <PlayerAvatar
        personId={info.id}
        name={info.name}
        size={240}
        displaySize={32}
        className={styles.chipAvatar}
      />
      <span className={styles.chipBody}>
        <span className={styles.chipInitials}>{initials}</span>
        {info.jersey && <span className={styles.chipJersey}>#{info.jersey}</span>}
      </span>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={() => onClick({ id: info.id, name: info.name, jersey: info.jersey, position: 'P' })}
        className={`${styles.chip} ${styles.chipPitcher} ${styles.chipButton}`}
        data-testid="pitch-log-pitcher-chip"
        aria-label={`Open profile for pitcher ${info.name}`}
        title={info.name}
      >
        {content}
      </button>
    );
  }
  return (
    <span
      className={`${styles.chip} ${styles.chipPitcher}`}
      data-testid="pitch-log-pitcher-chip"
      title={info.name}
    >
      {content}
    </span>
  );
}

export default function PitchLog({
  pitches,
  onResultClick,
  resultClickLabel = 'Open at-bat detail',
  atBatResult = null,
  batter = null,
  pitcher = null,
  onPlayerClick,
  onPrev,
  onNext,
  onLatest,
  isLatest = true,
  canPrev = false,
  canNext = false,
}: PitchLogProps): JSX.Element {
  const showNav = Boolean(onPrev || onNext || onLatest);
  return (
    <section aria-labelledby="pitch-log-heading" data-testid="pitch-log" className={styles.section}>
      <header className={styles.headerRow}>
        <div className={styles.headerInfo}>
          {batter && <BatterChip info={batter} onClick={onPlayerClick} />}
          <div className={styles.headerTitleBlock}>
            <h2 id="pitch-log-heading" className={styles.heading}>
              Pitches this at-bat
            </h2>
            <AtBatResultBadge result={atBatResult} />
          </div>
          {pitcher && <PitcherChip info={pitcher} onClick={onPlayerClick} />}
        </div>
        {showNav && (
          <nav className={styles.navRow} aria-label="At-bat navigation">
            <button
              type="button"
              className={styles.navButton}
              onClick={onPrev}
              disabled={!canPrev || !onPrev}
              aria-label="Previous at-bat"
              data-testid="pitch-log-prev"
            >
              ← Prev
            </button>
            <button
              type="button"
              className={styles.navButton}
              onClick={onNext}
              disabled={!canNext || !onNext}
              aria-label="Next at-bat"
              data-testid="pitch-log-next"
            >
              Next →
            </button>
            <button
              type="button"
              className={`${styles.navButton} ${styles.navLatest}`}
              onClick={onLatest}
              disabled={isLatest || !onLatest}
              aria-label="Jump to latest at-bat"
              data-testid="pitch-log-latest"
            >
              {isLatest ? 'Latest' : '↻ Latest'}
            </button>
          </nav>
        )}
      </header>
      {pitches.length === 0 ? (
        <p data-testid="pitch-log-empty" className={styles.empty}>No pitches yet in this at-bat.</p>
      ) : (
        // The rows have a fixed-track floor wider than a phone section, so they
        // scroll inside this wrapper rather than widening the document (see
        // `.tableScroll`). Header row + list share `.tableInner` so their
        // columns stay aligned while scrolled.
        <div className={styles.tableScroll} data-testid="pitch-log-table">
          <div className={styles.tableInner}>
            <div className={`${styles.row} ${styles.colHeader}`}>
              <span aria-hidden="true">#</span>
              <span>Pitch</span>
              <span className={styles.rightAlign}>Velo</span>
              <span className={styles.rightAlign}>Spin</span>
              <span className={styles.rightAlign}>Exit</span>
              <span className={styles.rightAlign}>LA</span>
              <span>Result</span>
            </div>
            <ol className={styles.list}>
              {pitches.map((p) => (
                <li
                  key={p.playId ?? `${p.index}-${p.displayNumber}`}
                  data-testid={`pitch-${p.displayNumber}`}
                  className={rowClassFor(p)}
                >
                  <span className={styles.index}>{p.displayNumber}</span>
                  <span className={styles.pitchType}>{p.type}</span>
                  <span className={styles.speed}>{fmtSpeed(p.speed)}</span>
                  <span
                    className={`${styles.statcastCell} ${p.spinRate == null ? styles.statcastMissing : ''}`}
                    data-testid={`pitch-${p.displayNumber}-spin`}
                  >
                    {fmt(p.spinRate)}
                  </span>
                  <span
                    className={`${styles.statcastCell} ${p.exitVelo == null ? styles.statcastMissing : ''}`}
                    data-testid={`pitch-${p.displayNumber}-exit`}
                  >
                    {fmt(p.exitVelo, ' mph', 1)}
                  </span>
                  <span
                    className={`${styles.statcastCell} ${p.launchAngle == null ? styles.statcastMissing : ''}`}
                    data-testid={`pitch-${p.displayNumber}-launch`}
                  >
                    {fmt(p.launchAngle, '°')}
                  </span>
                  <span className={styles.resultCell}>
                    {onResultClick ? (
                      <button
                        type="button"
                        onClick={onResultClick}
                        className={styles.resultButton}
                        data-testid={`pitch-${p.displayNumber}-result`}
                        aria-label={resultClickLabel}
                      >
                        {p.result}
                      </button>
                    ) : (
                      <span className={styles.result}>{p.result}</span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </section>
  );
}
